/**
 * Guía del alumno y representante (AUTONOMY · Layer 3).
 */
import { msg } from "@lingui/core/macro";
import { BookOpen } from "lucide-solid";

import type { HelpGuide } from "../types";
import { Bullets, GoTo, Note, P, Steps } from "../components/help-prose";

export const studentGuide: HelpGuide = {
  key: "help.student",
  slug: "alumno",
  title: msg`Student and guardian guide`,
  description: msg`Learn at your pace, follow your schedule, check in to class, hand in assignments, pay tuition and download your certificates.`,
  audience: msg`Students and guardians`,
  icon: BookOpen,
  sections: [
    {
      id: "aprender",
      title: msg`Learning`,
      summary: msg`Learn holds your enrolled courses: units, lessons, interactive exercises, videos and your progress — it works offline too.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Progress and streaks earn XP for the leaderboard leagues.`,
              msg`Videos remember where you stopped; watching enough of a video completes its block.`,
              msg`Everything you do offline syncs when you're back online.`,
            ]}
          />
          <GoTo to="/learn" label={msg`Open Learn`} />
        </>
      ),
    },
    // §M1/§M4 · la pantalla que explica los perfiles necesita a su vez estar
    // explicada: aquí vive el texto al que apunta su enlace contextual.
    {
      id: "mis-perfiles",
      title: msg`My profiles`,
      summary: msg`You can belong to several academies with a different role in each. "My profiles" shows, academy by academy, what you are, what that lets you do, and what it doesn't — and why.`,
      body: () => (
        <>
          <P
            msg={msg`Your role is per academy, not per account: you can be a student in one and a teacher in another. The switcher at the bottom of the sidebar changes which academy the app is showing you.`}
          />
          <Bullets
            items={[
              msg`Available: you can open that screen with your current role.`,
              msg`Needs another role: your role in that academy doesn't reach. Ask an academy admin — nobody can grant it to themselves.`,
              msg`Turned off by your academy: the capability exists and your data is intact, but the academy switched that feature off. A higher role won't bring it back; turning the feature on will.`,
            ]}
          />
          <Note
            msg={msg`Nothing is ever deleted when a feature is switched off: what you already have stays and comes back the moment it's switched on again.`}
          />
          <GoTo to="/profiles" label={msg`Open My profiles`} />
        </>
      ),
    },
    // B6 · NIVELES: la pantalla del camino tiene que explicarse, sobre todo el
    // motivo de un candado — sin eso, cada nivel bloqueado es un ticket.
    {
      id: "mi-ruta",
      title: msg`My path`,
      summary: msg`Your path shows the levels of your programme in order: finish one and the next opens. A locked level always tells you what's missing.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`What a "level" means is set by your academy: usually one course of a programme, but it can be a whole programme or a unit.`,
              msg`Each level shows an icon and a label, never colour alone: locked, open, in progress, completed or certified.`,
              msg`Tap a locked level to see exactly what's missing — the previous level, a payment, or a placement test.`,
              msg`A dashed connector means there's another way in, such as a placement test to skip ahead.`,
              msg`Finishing a level can give you a certificate for that level, XP and an offer for the next one.`,
            ]}
          />
          <GoTo to="/path" label={msg`Open my path`} />
        </>
      ),
    },
    {
      id: "catalogo",
      title: msg`Catalog and enrollment`,
      summary: msg`The catalog lists public courses. Enrolling in a cohort may be open, need approval, or place you on a waitlist when it's full.`,
      body: () => (
        <>
          <Note
            msg={msg`Waitlist: if a spot frees up, the first in line is promoted automatically and notified.`}
          />
          <GoTo to="/catalog" label={msg`Open Catalog`} />
        </>
      ),
    },
    {
      id: "aula",
      title: msg`Your classroom`,
      summary: msg`Each cohort you're enrolled in has a classroom: the course, your teachers, the next class, your progress, the cohort announcements and the plan of every class.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`My classrooms lists every cohort you're enrolled in.`,
              msg`Inside a classroom you reach the course modules, your assignments and each class plan.`,
              msg`It works offline: everything shown is already on your device.`,
            ]}
          />
          <GoTo to="/classroom" label={msg`Open My classrooms`} />
        </>
      ),
    },
    {
      // B2 · comunicación del aula. Se documenta lo que la gente pregunta:
      // qué ven de mí mis compañeros, por qué el chat no es instantáneo y qué
      // pasa con los mensajes viejos.
      id: "chat-aula",
      title: msg`Classmates and classroom chat`,
      summary: msg`Your classroom shows who else is in your cohort and has a chat for day-to-day questions. The forum is still where questions that deserve a lasting answer live.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Your classmates only see your name and your photo. Never your email, ID number, phone, enrolment status or grades.`,
              msg`Messages may take a few seconds to reach everyone: the chat syncs like the rest of your data, so you can also write it with no connection and it will be delivered when you reconnect.`,
              msg`Older messages leave the chat after a while and stay in the academy's records. Anything the teacher pins, or turns into a forum thread, stays visible.`,
              msg`Teachers can hide a message; hidden messages disappear from everyone's device, not just from the screen.`,
              msg`You can mute a classroom without muting the rest of the academy.`,
            ]}
          />
          <GoTo to="/classroom" label={msg`Open My classrooms`} />
        </>
      ),
    },
    {
      id: "expediente",
      title: msg`My record`,
      summary: msg`My record gathers in one place your progress, your grades with their weights, your attendance, your payments and your certificates.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Grades show each category and its weight, plus the grade projected from what has already been graded.`,
              msg`Attendance compares your percentage against the minimum your academy requires to certify.`,
              msg`Payments and certificates are the same screens you reach from the menu — nothing is duplicated.`,
            ]}
          />
          <GoTo to="/profile/academic" label={msg`Open My record`} />
        </>
      ),
    },
    {
      id: "horario",
      title: msg`Schedule and check-in`,
      summary: msg`Your schedule shows every upcoming class with room and campus. In in-person classes you can self-check-in with the code or QR the teacher shares.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Open Schedule to see your week; you can export it to your calendar app (ICS).`,
              msg`In class, open Check-in and enter the session code or scan the QR.`,
              msg`Live online classes are joined from the session page during the class window.`,
            ]}
          />
          <GoTo to="/schedule" label={msg`Open Schedule`} />
        </>
      ),
    },
    {
      id: "clase-invertida",
      title: msg`Class plan (before / during / after)`,
      summary: msg`When your academy uses the flipped classroom, each class has a plan: what to do BEFORE (watch a video, read a resource), DURING (join the live class) and AFTER (hand in the challenge).`,
      body: () => (
        <>
          <Steps
            items={[
              msg`From your schedule, open a class to see its plan.`,
              msg`BEFORE: complete the preparation — videos and readings show a check when done.`,
              msg`DURING: join the live class with the "Enter" button while it's in progress.`,
              msg`AFTER: hand in the assignment; the plan marks it done from your submission.`,
            ]}
          />
          <Note
            msg={msg`The ticks reuse your real progress: lessons, watched videos and submitted assignments. It all works offline.`}
          />
          <GoTo to="/schedule" label={msg`Open Schedule`} />
        </>
      ),
    },
    {
      id: "tareas",
      title: msg`Assignments and grades`,
      summary: msg`Assignments shows pending and graded work. Your final grade combines assignments, exercises and attendance as your academy configured.`,
      body: () => (
        <>
          <GoTo to="/assignments" label={msg`Open Assignments`} />
        </>
      ),
    },
    {
      id: "proyecto-final",
      title: msg`Final project`,
      summary: msg`The final project is your cohort's capstone. You see its rubric before you submit — the exact criteria, levels and points you'll be graded on — plus the deadline and a countdown.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Open Final project to read the published rubric and the instructions.`,
              msg`Submit your work as text or files before the deadline; late submissions are flagged.`,
              msg`When single correction is on, you get one shot — you can't resubmit after it's graded.`,
              msg`After grading you'll see your points per rubric criterion, feedback, and whether you passed.`,
            ]}
          />
          <Note
            msg={msg`Passing the final project can be a requirement to complete the cohort and earn your certificate.`}
          />
          <GoTo to="/final-project" label={msg`Open Final project`} />
        </>
      ),
    },
    {
      id: "reentrega",
      title: msg`Resubmit a challenge`,
      summary: msg`When a tutor returns your submission with feedback, you can address it and send a new version — every version and its feedback stay in the history.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Open the assignment: if the tutor returned it, you will see their feedback and a "Resubmit" box.`,
              msg`Address the feedback and send a new version — it goes back to the tutor's queue.`,
              msg`Repeat until the tutor approves it. Your academy may cap the number of versions.`,
            ]}
          />
          <Note
            msg={msg`Some assignments (like the final project) allow a single correction: there is no resubmission once returned.`}
          />
          <GoTo to="/assignments" label={msg`Open Assignments`} />
        </>
      ),
    },
    {
      id: "pagos",
      title: msg`Payments and debt`,
      summary: msg`Payments shows your invoices in dollars and bolívares with the rate of the day. Pay online or report an offline payment reference.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Each invoice shows due date and both currencies; overdue ones are highlighted.`,
              msg`Offline payments (cash, pago móvil, transfer, Zelle) are verified by the academy before they clear your debt.`,
              msg`Every verified payment has a printable receipt.`,
              // B4 · G3 — «Adelanta y ahorra» explicada donde el alumno la ve.
              msg`Pay ahead and save: once your first instalment is settled, you can pay the remaining months in one go with a discount. The card shows exactly what you save.`,
            ]}
          />
          <GoTo to="/payments" label={msg`Open Payments`} />
        </>
      ),
    },
    {
      id: "certificados",
      title: msg`Certificates`,
      summary: msg`When you complete and pass a cohort, your certificate is issued with a public verification link anyone can check.`,
      body: () => (
        <>
          <GoTo to="/learn" label={msg`Open Learn`} />
        </>
      ),
    },
    {
      id: "representantes",
      title: msg`Guardians`,
      summary: msg`For minors, the academy links a guardian account to the student. Guardians follow progress, attendance and payments of their children.`,
      body: () => (
        <>
          <Note
            msg={msg`Guardian links are managed by the academy staff on the student's record — ask at the front desk to add or change a guardian.`}
          />
          <GoTo to="/profile/academic" label={msg`Open your academic profile`} />
        </>
      ),
    },
    {
      id: "comunicaciones",
      title: msg`Announcements and notifications`,
      summary: msg`Announcements from your academy and cohort appear in the app; the bell collects notifications (grades, waitlist, forum replies).`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Control which non-essential emails you receive from Profile → Notification preferences.`,
              msg`Forum threads in your courses notify you when someone replies.`,
            ]}
          />
          <GoTo to="/notifications" label={msg`Open Notifications`} />
        </>
      ),
    },
    {
      id: "personalizacion",
      title: msg`Personalization and account`,
      summary: msg`Themes, fonts, language, keyboard shortcuts, multiple accounts and security (two-factor, sessions) live in your Profile.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`The app is available in 11 languages — each account keeps its own language.`,
              msg`Accepting an academy invitation links your account with the role the academy assigned you.`,
              msg`You can use several accounts (or a guest) and switch between them from the sidebar.`,
            ]}
          />
          <GoTo to="/profile" label={msg`Open Profile`} />
        </>
      ),
    },
  ],
};
